import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PipelineToolbar } from '../toolbar';
import { PipelineUI } from '../ui'; // Assuming this is the canvas
import { SubmitButton } from '../submit';
import client from '../api/client';
import { useStore } from '../store';
import { useToast } from '../components/ui/Toast';

// We need to bridge the existing 'ui.js' state with this page
// The existing app likely used global state or local state in App.js.
// I will check App.js content next to see how it was wired.
// For now, assume we can pass props or usage is similar.

const PipelineEditor = () => {
    const { id } = useParams();
    const isNew = id === 'new';
    const navigate = useNavigate();
    const { toast } = useToast();

    // This part depends heavily on how 'PipelineUI' works.
    // I need to read App.js to see how it was invoking PipelineUI.
    // Assuming PipelineUI handles the ReactFlow state internally or via a Store.
    // I see 'store.js' in the file list. It uses Zustand probably.

    const [title, setTitle] = useState("Untitled Pipeline");
    const [saving, setSaving] = useState(false);

    // Connect to Zustand store
    const { setNodes, setEdges } = useStore((state) => ({
        setNodes: state.setNodes,
        setEdges: state.setEdges
    }));

    useEffect(() => {
        const loadPipeline = async () => {
            try {
                const res = await client.get(`/pipelines/${id}`);
                const { title, nodes, edges } = res.data;
                setTitle(title);
                setNodes(nodes || []);
                setEdges(edges || []);
            } catch (err) {
                console.error("Failed to load pipeline", err);
                // Optionally redirect to dashboard or show error
            }
        };

        if (!isNew) {
            loadPipeline();
        } else {
            // Reset for new pipeline
            setNodes([]);
            setEdges([]);
            setTitle("Untitled Pipeline");
        }
    }, [id, isNew, setNodes, setEdges]);

    const handleSave = async () => {
        setSaving(true);
        const { nodes, edges } = useStore.getState();

        try {
            const pipelineData = {
                title,
                nodes,
                edges
            };

            if (isNew) {
                const res = await client.post('/pipelines/', pipelineData);
                // Redirect to the new ID to avoid creating duplicates on subsequent saves
                navigate(`/pipeline/${res.data._id || res.data.id}`, { replace: true });
            } else {
                await client.put(`/pipelines/${id}`, pipelineData);
            }
            toast.success('Pipeline saved successfully!', { title: 'Saved' });
        } catch (err) {
            console.error(err);
            toast.error('Failed to save pipeline. Please try again.', { title: 'Save Failed' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-cyber-black font-rajdhani">
            <div className="bg-cyber-gray border-b border-gray-800 px-4 py-2 flex justify-between items-center z-50 shadow-md">
                <div className="flex items-center space-x-4">
                    <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white transition-colors">
                        &larr; Back
                    </button>
                    <input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="text-lg font-bold text-white bg-transparent border-none focus:ring-0 font-orbitron tracking-wide"
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 active:bg-indigo-800 transition-colors disabled:opacity-50 font-orbitron tracking-wider text-sm shadow-[0_0_10px_rgba(99,102,241,0.4)]"
                    >
                        {saving ? 'Saving...' : 'Save Pipeline'}
                    </button>
                    <SubmitButton />
                </div>
            </div>

            <div className="flex-1 relative">
                {/* This is where the ReactFlow Canvas lives */}
                <PipelineToolbar />
                <PipelineUI />
            </div>
        </div>
    );
};

export default PipelineEditor;
